import React, { useState, useEffect, createContext, useContext, useRef, useCallback } from 'react';
import './App.css';
import enTranslations from './locales/en.json';
import heTranslations from './locales/he.json';

// Language Context
const LanguageContext = createContext();

// ===============================================
// CUSTOM BUTTON COMPONENTS
// ===============================================

// Ripple Effect Hook
const useRipple = () => {
  const [ripples, setRipples] = useState([]);

  const addRipple = useCallback((e) => {
    const button = e.currentTarget;
    const rect = button.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const ripple = { x, y, id: Date.now() };
    setRipples(prev => [...prev, ripple]);
    setTimeout(() => {
      setRipples(prev => prev.filter(r => r.id !== ripple.id));
    }, 600);
  }, []);

  return { ripples, addRipple };
};

// Primary CTA Button with Glow & Ripple
const GlowButton = ({ children, onClick, href, className = '', size = 'md', variant = 'primary' }) => {
  const { ripples, addRipple } = useRipple();
  const [isHovered, setIsHovered] = useState(false);

  const sizeClasses = {
    sm: 'py-2 px-4 text-sm',
    md: 'py-3 px-8 text-base',
    lg: 'py-4 px-10 text-lg',
    xl: 'py-5 px-12 text-xl'
  };

  const variantClasses = {
    primary: 'bg-gradient-to-r from-[#00ADEF] to-[#0077B6] hover:from-[#00C4FF] hover:to-[#00ADEF]',
    secondary: 'bg-gradient-to-r from-[#16203A] to-[#2D3A5C] hover:from-[#1E2D4D] hover:to-[#3D4A6C]',
    outline: 'bg-transparent border-2 border-[#00ADEF] hover:bg-[#00ADEF]/10',
    ghost: 'bg-white/10 hover:bg-white/20 backdrop-blur-sm'
  };

  const baseClasses = `
    relative overflow-hidden
    ${variantClasses[variant]}
    ${sizeClasses[size]}
    text-white font-bold rounded-full
    transition-all duration-300 ease-out
    transform hover:scale-105 active:scale-95
    focus:outline-none focus:ring-4 focus:ring-[#00ADEF]/50
    shadow-lg hover:shadow-2xl
    ${isHovered ? 'shadow-[#00ADEF]/40' : 'shadow-black/20'}
    ${className}
  `;

  const glowStyle = isHovered ? {
    boxShadow: '0 0 30px rgba(0, 173, 239, 0.5), 0 0 60px rgba(0, 173, 239, 0.3), 0 0 90px rgba(0, 173, 239, 0.1)'
  } : {};

  const content = (
    <>
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="ripple-effect"
          style={{ left: ripple.x, top: ripple.y }}
        />
      ))}
      <span className="relative z-10 flex items-center justify-center gap-2">
        {children}
      </span>
      {isHovered && (
        <span className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0 shimmer-effect" />
      )}
    </>
  );

  if (href) {
    return (
      <a
        href={href}
        className={baseClasses}
        style={glowStyle}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={addRipple}
      >
        {content}
      </a>
    );
  }

  return (
    <button
      className={baseClasses}
      style={glowStyle}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={(e) => { addRipple(e); onClick?.(e); }}
    >
      {content}
    </button>
  );
};

// Magnetic Button (follows cursor slightly)
const MagneticButton = ({ children, onClick, className = '' }) => {
  const buttonRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!buttonRef.current) return;
    const rect = buttonRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - rect.width / 2) * 0.3;
    const y = (e.clientY - rect.top - rect.height / 2) * 0.3;
    setPosition({ x, y });
  };

  const handleMouseLeave = () => {
    setPosition({ x: 0, y: 0 });
  };

  return (
    <button
      ref={buttonRef}
      className={`magnetic-button ${className}`}
      style={{ transform: `translate(${position.x}px, ${position.y}px)` }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onClick={onClick}
    >
      {children}
    </button>
  );
};

// Animated Border Button
const BorderButton = ({ children, onClick, href, className = '' }) => {
  const { ripples, addRipple } = useRipple();

  const content = (
    <>
      <span className="animated-border" />
      {ripples.map(ripple => (
        <span
          key={ripple.id}
          className="ripple-effect"
          style={{ left: ripple.x, top: ripple.y }}
        />
      ))}
      <span className="relative z-10">{children}</span>
    </>
  );

  const classes = `
    border-button relative overflow-hidden
    py-3 px-8 text-white font-bold rounded-lg
    bg-transparent transition-all duration-300
    hover:bg-[#00ADEF]/10 focus:outline-none focus:ring-2 focus:ring-[#00ADEF]
    ${className}
  `;

  if (href) {
    return (
      <a href={href} className={classes} onClick={addRipple}>
        {content}
      </a>
    );
  }

  return (
    <button className={classes} onClick={(e) => { addRipple(e); onClick?.(e); }}>
      {content}
    </button>
  );
};

// ===============================================
// SCROLL ANIMATION HOOK
// ===============================================

const useScrollAnimation = (options = {}) => {
  const ref = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          if (options.once !== false) {
            observer.unobserve(entry.target);
          }
        } else if (options.once === false) {
          setIsVisible(false);
        }
      },
      {
        threshold: options.threshold || 0.1,
        rootMargin: options.rootMargin || '0px'
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [options.threshold, options.rootMargin, options.once]);

  return [ref, isVisible];
};

// Animated Counter Hook
const useCountUp = (end, duration = 2000, isVisible = false) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isVisible) return;

    let startTime;
    const startValue = 0;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / duration, 1);
      const easeOutQuart = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(startValue + (end - startValue) * easeOutQuart));

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [end, duration, isVisible]);

  return count;
};

// Parallax Hook
const useParallax = (speed = 0.5) => {
  const ref = useRef(null);
  const [offset, setOffset] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const scrolled = window.scrollY;
      setOffset(scrolled * speed);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [speed]);

  return [ref, offset];
};

// Floating Particles Component
const FloatingParticles = ({ count = 50 }) => {
  const particles = Array.from({ length: count }, (_, i) => ({
    id: i,
    size: Math.random() * 4 + 1,
    x: Math.random() * 100,
    y: Math.random() * 100,
    duration: Math.random() * 20 + 10,
    delay: Math.random() * 5
  }));

  return (
    <div className="particles-container">
      {particles.map(particle => (
        <div
          key={particle.id}
          className="particle"
          style={{
            width: particle.size,
            height: particle.size,
            left: `${particle.x}%`,
            top: `${particle.y}%`,
            animationDuration: `${particle.duration}s`,
            animationDelay: `${particle.delay}s`
          }}
        />
      ))}
    </div>
  );
};

// Animated Section Wrapper
const AnimatedSection = ({ children, className = '', animation = 'fadeUp', delay = 0 }) => {
  const [ref, isVisible] = useScrollAnimation({ threshold: 0.1 });

  const animations = {
    fadeUp: 'animate-fade-up',
    fadeDown: 'animate-fade-down',
    fadeLeft: 'animate-fade-left',
    fadeRight: 'animate-fade-right',
    scaleUp: 'animate-scale-up',
    blur: 'animate-blur-in'
  };

  return (
    <div
      ref={ref}
      className={`${className} ${isVisible ? animations[animation] : 'opacity-0'}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
};

// Translations imported from JSON files
const translations = {
  en: enTranslations,
  he: heTranslations
};

// Custom Hook for Language
const useLanguage = () => useContext(LanguageContext);

// Professional Accessibility Widget Component
const AccessibilityWidget = () => {
  const { t, language } = useLanguage();
  const [isOpen, setIsOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('accessibilitySettings');
    return saved ? JSON.parse(saved) : {
      fontSize: 100,
      highContrast: false,
      invertColors: false,
      grayscale: false,
      underlineLinks: false,
      readableFont: false,
      bigCursor: false,
      stopAnimations: false,
      readingGuide: false,
      textSpacing: false,
    };
  });
  const [mouseY, setMouseY] = useState(0);

  // Save settings to localStorage
  useEffect(() => {
    localStorage.setItem('accessibilitySettings', JSON.stringify(settings));
  }, [settings]);

  // Apply settings
  useEffect(() => {
    document.documentElement.style.fontSize = `${settings.fontSize}%`;

    document.body.classList.toggle('acc-high-contrast', settings.highContrast);
    document.body.classList.toggle('acc-invert-colors', settings.invertColors);
    document.body.classList.toggle('acc-grayscale', settings.grayscale);
    document.body.classList.toggle('acc-underline-links', settings.underlineLinks);
    document.body.classList.toggle('acc-readable-font', settings.readableFont);
    document.body.classList.toggle('acc-big-cursor', settings.bigCursor);
    document.body.classList.toggle('acc-stop-animations', settings.stopAnimations);
    document.body.classList.toggle('acc-text-spacing', settings.textSpacing);
  }, [settings]);

  // Reading guide mouse tracking
  useEffect(() => {
    if (settings.readingGuide) {
      const handleMouseMove = (e) => setMouseY(e.clientY);
      window.addEventListener('mousemove', handleMouseMove);
      return () => window.removeEventListener('mousemove', handleMouseMove);
    }
  }, [settings.readingGuide]);

  const updateSetting = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const resetAll = () => {
    setSettings({
      fontSize: 100,
      highContrast: false,
      invertColors: false,
      grayscale: false,
      underlineLinks: false,
      readableFont: false,
      bigCursor: false,
      stopAnimations: false,
      readingGuide: false,
      textSpacing: false,
    });
  };

  const isRTL = language === 'he';

  return (
    <>
      {/* Reading Guide */}
      {settings.readingGuide && (
        <div
          className="fixed left-0 right-0 h-12 pointer-events-none z-[9998] border-t-2 border-b-2 border-[#00ADEF] bg-[#00ADEF]/10"
          style={{ top: mouseY - 24 }}
          aria-hidden="true"
        />
      )}

      {/* Widget Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed ${isRTL ? 'right-0' : 'left-0'} top-32 z-[9999] bg-[#00ADEF] hover:bg-blue-600 text-white p-3 ${isRTL ? 'rounded-l-lg' : 'rounded-r-lg'} shadow-lg transition-all focus:outline-none focus:ring-4 focus:ring-blue-400`}
        aria-expanded={isOpen}
        aria-label={t('accessibilityTitle')}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-7 w-7" fill="currentColor" viewBox="0 0 24 24">
          <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
        </svg>
      </button>

      {/* Widget Panel */}
      {isOpen && (
        <div
          className={`fixed ${isRTL ? 'right-0' : 'left-0'} top-32 z-[9998] bg-white shadow-2xl ${isRTL ? 'rounded-l-xl' : 'rounded-r-xl'} w-80 max-h-[80vh] overflow-y-auto border-2 border-[#00ADEF]`}
          role="dialog"
          aria-modal="true"
          aria-label={t('accessibilityTitle')}
          dir={isRTL ? 'rtl' : 'ltr'}
        >
          {/* Header */}
          <div className="bg-[#00ADEF] text-white p-4 flex items-center justify-between sticky top-0">
            <h2 className="text-lg font-bold flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 2C13.1 2 14 2.9 14 4C14 5.1 13.1 6 12 6C10.9 6 10 5.1 10 4C10 2.9 10.9 2 12 2ZM21 9H15V22H13V16H11V22H9V9H3V7H21V9Z"/>
              </svg>
              {t('accessibilityTitle')}
            </h2>
            <button
              onClick={() => setIsOpen(false)}
              className="p-1 hover:bg-white/20 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-white"
              aria-label={t('close')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Controls */}
          <div className="p-4 space-y-4">
            {/* Font Size */}
            <div className="bg-gray-50 p-3 rounded-lg">
              <label className="block text-sm font-medium text-gray-700 mb-2">{t('fontSize')}</label>
              <div className="flex items-center justify-between gap-2">
                <button
                  onClick={() => updateSetting('fontSize', Math.max(70, settings.fontSize - 10))}
                  className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 rounded text-lg font-bold focus:outline-none focus:ring-2 focus:ring-[#00ADEF]"
                  aria-label={t('decreaseFontSize')}
                >
                  A-
                </button>
                <span className="px-3 py-2 bg-white rounded border min-w-[60px] text-center font-medium">
                  {settings.fontSize}%
                </span>
                <button
                  onClick={() => updateSetting('fontSize', Math.min(200, settings.fontSize + 10))}
                  className="flex-1 py-2 bg-gray-200 hover:bg-gray-300 rounded text-lg font-bold focus:outline-none focus:ring-2 focus:ring-[#00ADEF]"
                  aria-label={t('increaseFontSize')}
                >
                  A+
                </button>
              </div>
            </div>

            {/* Toggle Buttons */}
            {[
              { key: 'highContrast', icon: '◐' },
              { key: 'invertColors', icon: '🔄' },
              { key: 'grayscale', icon: '◑' },
              { key: 'underlineLinks', icon: '🔗' },
              { key: 'readableFont', icon: 'Aa' },
              { key: 'bigCursor', icon: '👆' },
              { key: 'stopAnimations', icon: '⏸' },
              { key: 'readingGuide', icon: '📖' },
              { key: 'textSpacing', icon: '↔' },
            ].map(({ key, icon }) => (
              <button
                key={key}
                onClick={() => updateSetting(key, !settings[key])}
                className={`w-full py-3 px-4 rounded-lg text-sm font-medium transition-all flex items-center gap-3 focus:outline-none focus:ring-2 focus:ring-[#00ADEF] ${
                  settings[key]
                    ? 'bg-[#00ADEF] text-white shadow-md'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                aria-pressed={settings[key]}
              >
                <span className="text-lg" aria-hidden="true">{icon}</span>
                {t(key)}
              </button>
            ))}

            {/* Reset Button */}
            <button
              onClick={resetAll}
              className="w-full py-3 px-4 rounded-lg bg-red-500 text-white font-medium hover:bg-red-600 transition-colors flex items-center justify-center gap-2 focus:outline-none focus:ring-2 focus:ring-red-400"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('reset')}
            </button>
          </div>
        </div>
      )}
    </>
  );
};

// Cookie Consent Component
const CookieConsent = () => {
  const { t, language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    const consent = { necessary: true, analytics: true, marketing: true, timestamp: Date.now() };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const consent = { necessary: true, analytics: false, marketing: false, timestamp: Date.now() };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    const consent = { ...preferences, timestamp: Date.now() };
    localStorage.setItem('cookieConsent', JSON.stringify(consent));
    setIsVisible(false);
    setShowSettings(false);
  };

  if (!isVisible) return null;

  const isRTL = language === 'he';

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[9997] bg-white shadow-2xl border-t-4 border-[#00ADEF]"
      role="dialog"
      aria-modal="true"
      aria-label={t('cookieTitle')}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto p-6">
        {!showSettings ? (
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="text-lg font-bold text-gray-800 mb-2 flex items-center gap-2">
                <span aria-hidden="true">🍪</span>
                {t('cookieTitle')}
              </h3>
              <p className="text-gray-600 text-sm">{t('cookieText')}</p>
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => setShowSettings(true)}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#00ADEF]"
              >
                {t('cookieSettings')}
              </button>
              <button
                onClick={handleRejectAll}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#00ADEF]"
              >
                {t('cookieReject')}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-6 py-2 bg-[#00ADEF] text-white rounded-lg hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {t('cookieAccept')}
              </button>
            </div>
          </div>
        ) : (
          <div>
            <h3 className="text-lg font-bold text-gray-800 mb-4">{t('cookieSettings')}</h3>
            <div className="space-y-4 mb-6">
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-800">{t('cookieNecessary')}</h4>
                  <p className="text-sm text-gray-500">{t('cookieNecessaryDesc')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={true}
                  disabled
                  className="w-5 h-5 accent-[#00ADEF]"
                />
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-800">{t('cookieAnalytics')}</h4>
                  <p className="text-sm text-gray-500">{t('cookieAnalyticsDesc')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.analytics}
                  onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                  className="w-5 h-5 accent-[#00ADEF]"
                />
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div>
                  <h4 className="font-medium text-gray-800">{t('cookieMarketing')}</h4>
                  <p className="text-sm text-gray-500">{t('cookieMarketingDesc')}</p>
                </div>
                <input
                  type="checkbox"
                  checked={preferences.marketing}
                  onChange={(e) => setPreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                  className="w-5 h-5 accent-[#00ADEF]"
                />
              </div>
            </div>

            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#00ADEF]"
              >
                {t('close')}
              </button>
              <button
                onClick={handleSavePreferences}
                className="px-6 py-2 bg-[#00ADEF] text-white rounded-lg hover:bg-blue-600 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-400"
              >
                {t('cookieSave')}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Privacy Policy Modal
const PrivacyPolicyModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();

  if (!isOpen) return null;

  const isRTL = language === 'he';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="privacy-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="sticky top-0 bg-[#00ADEF] text-white p-4 flex items-center justify-between rounded-t-xl">
          <h2 id="privacy-title" className="text-xl font-bold">{t('privacyTitle')}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-white"
            aria-label={t('close')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-gray-700">{t('privacyIntro')}</p>

          <div>
            <h3 className="text-lg font-bold text-[#16203A] mb-2">{t('privacyCollect')}</h3>
            <p className="text-gray-600">{t('privacyCollectText')}</p>
          </div>

          <div>
            <h3 className="text-lg font-bold text-[#16203A] mb-2">{t('privacyUse')}</h3>
            <p className="text-gray-600">{t('privacyUseText')}</p>
          </div>

          <div>
            <h3 className="text-lg font-bold text-[#16203A] mb-2">{t('privacyProtect')}</h3>
            <p className="text-gray-600">{t('privacyProtectText')}</p>
          </div>

          <div>
            <h3 className="text-lg font-bold text-[#16203A] mb-2">{t('privacyRights')}</h3>
            <p className="text-gray-600">{t('privacyRightsText')}</p>
          </div>

          <div>
            <h3 className="text-lg font-bold text-[#16203A] mb-2">{t('privacyContact')}</h3>
            <p className="text-gray-600">{t('privacyContactText')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Accessibility Statement Modal
const AccessibilityStatementModal = ({ isOpen, onClose }) => {
  const { t, language } = useLanguage();

  if (!isOpen) return null;

  const isRTL = language === 'he';

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[10000] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="accessibility-statement-title"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        dir={isRTL ? 'rtl' : 'ltr'}
      >
        <div className="sticky top-0 bg-[#00ADEF] text-white p-4 flex items-center justify-between rounded-t-xl">
          <h2 id="accessibility-statement-title" className="text-xl font-bold">{t('accessStatementTitle')}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-white/20 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-white"
            aria-label={t('close')}
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-6 space-y-6">
          <p className="text-gray-700">{t('accessStatementIntro')}</p>

          <div>
            <h3 className="text-lg font-bold text-[#16203A] mb-2">{t('accessStatementMeasures')}</h3>
            <p className="text-gray-600">{t('accessStatementMeasuresText')}</p>
          </div>

          <div>
            <h3 className="text-lg font-bold text-[#16203A] mb-2">{t('accessStatementFeatures')}</h3>
            <p className="text-gray-600">{t('accessStatementFeaturesText')}</p>
          </div>

          <div>
            <h3 className="text-lg font-bold text-[#16203A] mb-2">{t('accessStatementContact')}</h3>
            <p className="text-gray-600">{t('accessStatementContactText')}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

// Language Switcher Component
const LanguageSwitcher = () => {
  const { language, setLanguage, t } = useLanguage();

  return (
   <div
  className="
    absolute top-4
    ltr:right-4 rtl:left-4
    flex items-center gap-2
  "
>


  <button
    onClick={() => setLanguage(language === 'he' ? 'en' : 'he')}
    className="flex items-center gap-2 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-white"
    aria-label={language === 'he' ? t('switchToEnglish') : t('switchToHebrew')}
  >
    <span className="text-white font-medium">
      {language === 'he' ? 'EN' : 'עב'}
    </span>

    <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h12M9 3v2m1.048 9.5A18.022 18.022 0 016.412 9m6.088 9h7M11 21l5-10 5 10M12.751 5C11.783 10.77 8.07 15.61 3 18.129" />
    </svg>
  </button>
</div>

  );
};

// Header Component
const Header = () => {
  const { t, language } = useLanguage();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 80);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#about', label: t('about') },
    { href: '#services', label: t('services') },
    { href: '#solutions', label: t('solutions') },
    { href: '#customers', label: t('customers') },
  ];

  const isRTL = language === 'he';

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-300 ${
        isScrolled ? 'bg-[#16203A]/98 shadow-lg py-2' : 'bg-transparent py-4'
      }`}
      role="banner"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <nav className={`container mx-auto px-4 flex items-center justify-between ${isRTL ? 'flex-row-reverse' : ''}`} role="navigation" aria-label={t('mainNavigation')}>
        

        {/* Desktop Navigation */}
        <ul className={`hidden md:flex items-center gap-6  ${isRTL ? 'flex-row-reverse' : ''}`}>
          {navLinks.map((link) => (
            <li key={link.href}>
              <a
                href={link.href}
                className="text-[#00ADEF] hover:text-white transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-[#00ADEF] rounded px-2 py-1"
              >
                {link.label}
              </a>
            </li>
          ))}
          <li>
            <LanguageSwitcher />
          </li>
        </ul>

        {/* Mobile Menu Button */}
        <div className={`flex items-center gap-4 md:hidden ${isRTL ? 'flex-row-reverse' : ''}`}>
          <LanguageSwitcher />
        <button
  className="
    absolute top-4
    rtl:right-4 ltr:left-4
    text-white p-2
    focus:outline-none focus:ring-2 focus:ring-[#00ADEF]
    rounded
  "
  onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
  aria-expanded={isMobileMenuOpen}
  aria-label={t('toggleMobileMenu')}
>
  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    {isMobileMenuOpen ? (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    ) : (
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    )}
  </svg>
</button>

        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <div className="absolute top-full left-0 right-0 bg-[#16203A]/98 md:hidden">
            <ul className="flex flex-col py-4">
              {navLinks.map((link) => (
                <li key={link.href}>
                  <a
                    href={link.href}
                    className="block px-4 py-3 text- hover:text-[#00ADEF] hover:bg-white/10 transition-colors"
                    onClick={() => setIsMobileMenuOpen(false)}
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        )}
      </nav>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'he';
  const [parallaxRef, parallaxOffset] = useParallax(0.3);
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section
      id="hero"
      ref={parallaxRef}
      className="relative h-screen flex items-center justify-center overflow-hidden"
      aria-label={t('heroSection')}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Video Background with Parallax */}
      <video
  autoPlay
  muted
  loop
  playsInline
  className="absolute inset-0 w-full h-full object-cover md:hidden"
  style={{ transform: `translateY(${parallaxOffset * 0.5}px) scale(1.1)` }}
  aria-hidden="true"
>
  <source src="/video_mobile.mp4" type="video/mp4" />
</video>

<video
  autoPlay
  muted
  loop
  playsInline
  className="absolute inset-0 w-full h-full object-cover hidden md:block"
  style={{ transform: `translateY(${parallaxOffset * 0.5}px) scale(1.1)` }}
  aria-hidden="true"
>
  <source src="/video.mp4" type="video/mp4" />
</video>

      {/* Gradient Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#16203A]/80 via-[#16203A]/60 to-[#16203A]/90" aria-hidden="true"></div>

      {/* Animated Particles */}
      <FloatingParticles count={40} />

      {/* Animated Grid Pattern */}
      <div className="absolute inset-0 hero-grid-pattern opacity-20" aria-hidden="true"></div>

      {/* Glowing Orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-[#00ADEF]/20 rounded-full blur-3xl animate-pulse-slow" aria-hidden="true"></div>
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-[#0077B6]/20 rounded-full blur-3xl animate-pulse-slow animation-delay-2000" aria-hidden="true"></div>

      {/* Content */}
      <div ref={titleRef} className="relative z-10 text-center px-4 max-w-4xl mx-auto">
        {/* Animated Badge */}
        



        {/* CTA Buttons */}
        <div className={`flex flex-col sm:flex-row items-center justify-center gap-4 mt-[80vh] ${titleVisible ? 'animate-fade-up animation-delay-600' : 'opacity-0'}`}>
          <GlowButton href="#about" size="lg" variant="primary">
            {t('aboutUs')}
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </GlowButton>
          <BorderButton href="#services">
            {t('services')}
          </BorderButton>
        </div>

        {/* Trust Badges */}
        <div className={`mt-12 flex items-center justify-center gap-8 ${titleVisible ? 'animate-fade-up animation-delay-800' : 'opacity-0'}`}>
          <div className="text-center">
            <div className="text-3xl font-bold text-white">25+</div>
            <div className="text-white/60 text-sm">Years Experience</div>
          </div>
          <div className="w-px h-12 bg-white/20"></div>
          <div className="text-center">
            <div className="text-3xl font-bold text-white">100+</div>
            <div className="text-white/60 text-sm">Projects Delivered</div>
          </div>
          <div className="w-px h-12 bg-white/20"></div>
          <div className="text-center">
            <div className="text-3xl font-bold text-white">24/7</div>
            <div className="text-white/60 text-sm">Support Available</div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2" aria-hidden="true">
        <div className="scroll-indicator">
          <div className="scroll-indicator-dot"></div>
        </div>
        <p className="text-white/50 text-xs mt-2 text-center">Scroll to explore</p>
      </div>

      {/* Side Decorations */}
      <div className="absolute left-8 top-1/2 transform -translate-y-1/2 hidden lg:flex flex-col gap-4" aria-hidden="true">
        <div className="w-px h-20 bg-gradient-to-b from-transparent via-[#00ADEF] to-transparent"></div>
        <div className="text-white/40 text-xs writing-vertical">IRONCLAD SYSTEMS</div>
        <div className="w-px h-20 bg-gradient-to-b from-transparent via-[#00ADEF] to-transparent"></div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'he';
  const [titleRef, titleVisible] = useScrollAnimation({ threshold: 0.2 });
  const [contentRef, contentVisible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section
      id="about"
      className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
      aria-labelledby="about-heading"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Decorative Elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-[#00ADEF]/5 rounded-full blur-3xl" aria-hidden="true"></div>
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#16203A]/5 rounded-full blur-3xl" aria-hidden="true"></div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={titleRef} className={`text-center mb-16 ${titleVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="inline-block px-4 py-2 bg-[#00ADEF]/10 text-[#00ADEF] rounded-full text-sm font-medium mb-4">
            Who We Are
          </span>
          <h2 id="about-heading" className="text-4xl md:text-5xl font-bold text-[#16203A] font-oswald">
            {t('aboutTitle')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00ADEF] to-[#0077B6] mx-auto mt-6 rounded-full"></div>
        </div>

        <div ref={contentRef} className="max-w-4xl mx-auto">
          <div className={`bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 ${contentVisible ? 'animate-fade-up animation-delay-200' : 'opacity-0'}`}>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              {t('aboutP1')}
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              {t('aboutP2')}
            </p>

            {/* Features Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10 pt-10 border-t border-gray-100">
              {[
                { icon: '🎯', label: 'Innovation' },
                { icon: '🤝', label: 'Partnership' },
                { icon: '🔐', label: 'Security' },
                { icon: '⚡', label: 'Excellence' }
              ].map((item, index) => (
                <div key={index} className="text-center group cursor-pointer">
                  <div className="text-3xl mb-2 transform group-hover:scale-110 transition-transform">{item.icon}</div>
                  <div className="text-sm font-medium text-gray-600 group-hover:text-[#00ADEF] transition-colors">{item.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Statistics Section
const StatisticsSection = () => {
  const { t } = useLanguage();
  const [sectionRef, isVisible] = useScrollAnimation({ threshold: 0.2 });

  const stats = [
    { value: 70, suffix: '%', labelKey: 'stat1Label', icon: '🛡️' },
    { value: 83, suffix: '%', labelKey: 'stat2Label', icon: '🤖' },
    { value: 44, suffix: '%', labelKey: 'stat3Label', icon: '📱' },
    { value: 4900, suffix: '', labelKey: 'stat4Label', icon: '🦠', format: true },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-20 bg-gradient-to-br from-[#16203A] via-[#0a1628] to-[#16203A] relative overflow-hidden"
      aria-labelledby="stats-heading"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 opacity-30" aria-hidden="true">
        <div className="absolute top-0 left-1/4 w-72 h-72 bg-[#00ADEF]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#0077B6]/10 rounded-full blur-3xl"></div>
      </div>

      <h2 id="stats-heading" className="sr-only">Security Statistics</h2>
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <StatCard
              key={index}
              stat={stat}
              isVisible={isVisible}
              delay={index * 150}
              t={t}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

// Individual Stat Card with Animation
const StatCard = ({ stat, isVisible, delay, t }) => {
  const count = useCountUp(stat.value, 2000, isVisible);
  const displayValue = stat.format ? count.toLocaleString() : count;

  return (
    <div
      className={`stat-card text-center p-8 rounded-2xl bg-white/5 backdrop-blur-sm border border-white/10 hover:border-[#00ADEF]/50 transition-all duration-500 hover:transform hover:-translate-y-2 group ${isVisible ? 'animate-fade-up' : 'opacity-0'}`}
      style={{ animationDelay: `${delay}ms` }}
    >
      <div className="text-4xl mb-4 transform group-hover:scale-110 transition-transform" aria-hidden="true">
        {stat.icon}
      </div>
      <div className="text-5xl md:text-6xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#00ADEF] to-[#00C4FF] mb-4">
        {displayValue}{stat.suffix}
      </div>
      <p className="text-white/70 text-sm leading-relaxed">{t(stat.labelKey)}</p>
      <div className="mt-4 w-16 h-1 bg-gradient-to-r from-[#00ADEF] to-transparent mx-auto rounded-full"></div>
    </div>
  );
};

// Services Section
const ServicesSection = () => {
  const { t, language } = useLanguage();
  const [activeModal, setActiveModal] = useState(null);
  const isRTL = language === 'he';
  const [sectionRef, sectionVisible] = useScrollAnimation({ threshold: 0.1 });

  const services = [
    { id: 'consulting', icon: '🛡️', titleKey: 'consulting', descKey: 'consultingDesc', color: 'from-blue-500 to-cyan-500' },
    { id: 'solutions', icon: '🔒', titleKey: 'secSolutions', descKey: 'secSolutionsDesc', color: 'from-purple-500 to-pink-500' },
    { id: 'training', icon: '👨‍💻', titleKey: 'training', descKey: 'trainingDesc', color: 'from-orange-500 to-red-500' },
    { id: 'development', icon: '⚙️', titleKey: 'development', descKey: 'developmentDesc', color: 'from-green-500 to-teal-500' },
  ];

  return (
    <section
      id="services"
      ref={sectionRef}
      className="py-24 bg-gradient-to-b from-gray-50 to-white relative overflow-hidden"
      aria-labelledby="services-heading"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5" aria-hidden="true">
        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #00ADEF 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`text-center mb-16 ${sectionVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="inline-block px-4 py-2 bg-[#00ADEF]/10 text-[#00ADEF] rounded-full text-sm font-medium mb-4">
            {t('whatWeOffer')}
          </span>
          <h2 id="services-heading" className="text-4xl md:text-5xl font-bold text-[#16203A] font-oswald mb-4">
            {t('servicesTitle')}
          </h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-lg">
            {t('servicesDesc')}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00ADEF] to-[#0077B6] mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {services.map((service, index) => (
            <div
              key={service.id}
              className={`service-card group relative bg-white rounded-3xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 border border-gray-100 hover:border-transparent ${sectionVisible ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: `${index * 100 + 200}ms` }}
            >
              {/* Gradient Border on Hover */}
              <div className={`absolute inset-0 rounded-3xl bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-xl`}></div>
              <div className="absolute inset-0 rounded-3xl bg-white group-hover:bg-white/95 transition-colors duration-500"></div>

              <div className="relative z-10">
                <div className="w-16 h-16 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-[#00ADEF]/10 to-[#0077B6]/10 flex items-center justify-center transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500">
                  <span className="text-4xl" aria-hidden="true">{service.icon}</span>
                </div>

                <h3 className="text-xl font-bold text-[#16203A] text-center mb-4 group-hover:text-[#00ADEF] transition-colors">
                  {t(service.titleKey)}
                </h3>

                <div className="w-12 h-0.5 bg-gradient-to-r from-[#00ADEF] to-transparent mx-auto mb-6 group-hover:w-full transition-all duration-500"></div>

                <GlowButton
                  onClick={() => setActiveModal(service.id)}
                  size="sm"
                  variant="primary"
                  className="w-full"
                >
                  {t('readMore')}
                  <svg className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </GlowButton>
              </div>
            </div>
          ))}
        </div>

        {/* Modal */}
        {activeModal && (
          <div
            className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            role="dialog"
            aria-modal="true"
            aria-labelledby="modal-title"
            onClick={(e) => e.target === e.currentTarget && setActiveModal(null)}
          >
            <div
              className="bg-white rounded-3xl max-w-lg w-full p-8 relative max-h-[90vh] overflow-y-auto shadow-2xl animate-scale-up"
              dir={isRTL ? 'rtl' : 'ltr'}
            >
              <button
                onClick={() => setActiveModal(null)}
                className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} w-10 h-10 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-500 hover:text-gray-700 flex items-center justify-center transition-all focus:outline-none focus:ring-2 focus:ring-[#00ADEF]`}
                aria-label={t('close')}
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>

              {services.filter(s => s.id === activeModal).map(service => (
                <div key={service.id} className="text-center">
                  <div className={`w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br ${service.color} flex items-center justify-center shadow-lg`}>
                    <span className="text-4xl" aria-hidden="true">{service.icon}</span>
                  </div>
                  <h3 id="modal-title" className="text-2xl font-bold text-[#16203A] mb-6">
                    {t(service.titleKey)}
                  </h3>
                  <p className="text-gray-600 leading-relaxed text-left">{t(service.descKey)}</p>

                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <GlowButton onClick={() => setActiveModal(null)} size="md" variant="primary">
                      Close
                    </GlowButton>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

// Solutions Section
const SolutionsSection = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'he';
  const [sectionRef, sectionVisible] = useScrollAnimation({ threshold: 0.1 });

  return (
    <section
      id="solutions"
      ref={sectionRef}
      className="py-24 bg-gradient-to-br from-[#16203A] via-[#1E2D4D] to-[#16203A] relative overflow-hidden"
      aria-labelledby="solutions-heading"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Effects */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute top-0 left-0 w-full h-full opacity-10" style={{ backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%2300ADEF\' fill-opacity=\'0.4\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")' }}></div>
        <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-[#00ADEF]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/4 left-1/4 w-64 h-64 bg-[#0077B6]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`text-center mb-16 ${sectionVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-4 backdrop-blur-sm">
            Our Approach
          </span>
          <h2 id="solutions-heading" className="text-4xl md:text-5xl font-bold text-white font-oswald mb-4">
            {t('solutionsTitle')}
          </h2>
          <h3 className="text-2xl text-[#00ADEF] font-medium">
            {t('solutionsSubtitle')}
          </h3>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00ADEF] to-[#0077B6] mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="max-w-4xl mx-auto">
          <div className={`bg-white/5 backdrop-blur-sm rounded-3xl p-8 md:p-12 border border-white/10 ${sectionVisible ? 'animate-fade-up animation-delay-200' : 'opacity-0'}`}>
            <p className="text-lg text-white/80 mb-8 leading-relaxed">
              {t('solutionsP1')}
            </p>

            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#00ADEF] to-[#0077B6] flex items-center justify-center flex-shrink-0">
                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              </div>
              <h4 className="text-xl font-bold text-[#00ADEF]">{t('techSolutions')}</h4>
            </div>

            <div className="space-y-4 text-white/70 leading-relaxed">
              <p>{t('solutionsP2')}</p>
              <p>{t('solutionsP3')}</p>
              <p>{t('solutionsP4')}</p>
            </div>

            {/* CTA */}
            <div className={`mt-10 pt-8 border-t border-white/10 ${sectionVisible ? 'animate-fade-up animation-delay-400' : 'opacity-0'}`}>
              <GlowButton href="#contact" size="lg" variant="primary">
                Get Started
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                </svg>
              </GlowButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Products Section
const ProductsSection = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'he';
  const [sectionRef, sectionVisible] = useScrollAnimation({ threshold: 0.1 });

  const products = [
    { name: 'Fortinet', color: '#EE3124', gradient: 'from-red-500 to-orange-500' },
    { name: 'F5', color: '#E4002B', gradient: 'from-red-600 to-red-500' },
    { name: 'Cynet', color: '#00B4E6', gradient: 'from-cyan-500 to-blue-500' },
    { name: 'Symantec', color: '#FDB511', gradient: 'from-yellow-500 to-orange-500' },
    { name: 'Check Point', color: '#E21F26', gradient: 'from-red-500 to-pink-500' },
    { name: 'Palo Alto', color: '#FA582D', gradient: 'from-orange-500 to-red-500' },
    { name: 'Teramind', color: '#2D9CDB', gradient: 'from-blue-400 to-cyan-500' },
    { name: 'Netwrix', color: '#009FDA', gradient: 'from-blue-500 to-cyan-400' },
  ];

  return (
    <section
      ref={sectionRef}
      className="py-24 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden"
      aria-labelledby="products-heading"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="container mx-auto px-4">
        <div className={`text-center mb-16 ${sectionVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="inline-block px-4 py-2 bg-[#00ADEF]/10 text-[#00ADEF] rounded-full text-sm font-medium mb-4">
            Trusted Partners
          </span>
          <h2 id="products-heading" className="text-4xl md:text-5xl font-bold text-[#16203A] font-oswald mb-4">
            {t('productsTitle')}
          </h2>
          <p className="text-gray-600 max-w-2xl mx-auto text-lg">
            {t('productsDesc')}
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00ADEF] to-[#0077B6] mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {products.map((product, index) => (
            <div
              key={product.name}
              className={`product-card group relative bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 flex items-center justify-center h-32 cursor-pointer overflow-hidden ${sectionVisible ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: `${index * 75 + 200}ms` }}
            >
              {/* Hover Gradient Background */}
              <div className={`absolute inset-0 bg-gradient-to-br ${product.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`}></div>

              {/* Bottom Accent Line */}
              <div
                className="absolute bottom-0 left-0 right-0 h-1 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left"
                style={{ backgroundColor: product.color }}
              ></div>

              <span className="relative z-10 text-xl font-bold text-gray-700 group-hover:text-white transition-colors duration-500">
                {product.name}
              </span>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Customers Section
const CustomersSection = () => {
  const { t, language } = useLanguage();
  const isRTL = language === 'he';
  const [sectionRef, sectionVisible] = useScrollAnimation({ threshold: 0.1 });

  const customers = [
    { name: 'Keshet', logo: '/keshet.png' },
    { name: 'Comax', logo: '/comax.png' },
    { name: 'Dexcel', logo: '/dexcel.png' },
    { name: 'UPS', logo: '/ups.png' },
  ];

  return (
    <section
      id="customers"
      ref={sectionRef}
      className="py-24 bg-gradient-to-br from-[#16203A] via-[#0a1628] to-[#16203A] relative overflow-hidden"
      aria-labelledby="customers-heading"
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      {/* Background Effects */}
      <div className="absolute inset-0" aria-hidden="true">
        <div className="absolute top-1/3 left-0 w-64 h-64 bg-[#00ADEF]/10 rounded-full blur-3xl"></div>
        <div className="absolute bottom-1/3 right-0 w-96 h-96 bg-[#0077B6]/10 rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div className={`text-center mb-16 ${sectionVisible ? 'animate-fade-up' : 'opacity-0'}`}>
          <span className="inline-block px-4 py-2 bg-white/10 text-white rounded-full text-sm font-medium mb-4 backdrop-blur-sm">
            {t('trustedByIndustryLeaders')}
          </span>
          <h2 id="customers-heading" className="text-4xl md:text-5xl font-bold text-white font-oswald">
            {t('customersTitle')}
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-[#00ADEF] to-[#0077B6] mx-auto mt-6 rounded-full"></div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {customers.map((customer, index) => (
            <div
              key={customer.name}
              className={`customer-card group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl flex items-center justify-center h-36 transition-all duration-500 hover:-translate-y-2 ${sectionVisible ? 'animate-fade-up' : 'opacity-0'}`}
              style={{ animationDelay: `${index * 100 + 200}ms` }}
            >
              <img
                src={customer.logo}
                alt={`${customer.name} ${t('customerLogoAlt')}`}
                className="max-h-20 max-w-full object-contain filter grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-110"
              />
            </div>
          ))}
        </div>

        {/* Testimonial Teaser */}
        <div className={`mt-16 text-center ${sectionVisible ? 'animate-fade-up animation-delay-600' : 'opacity-0'}`}>
          <p className="text-white/60 text-lg max-w-2xl mx-auto">
            {t('trustedBy')}
          </p>
        </div>
      </div>
    </section>
  );
};

// Footer Component
const Footer = ({ onPrivacyClick, onAccessibilityClick }) => {
  const { t, language } = useLanguage();
  const isRTL = language === 'he';

  return (
    <footer className="bg-white py-12 border-t" role="contentinfo" dir={isRTL ? 'rtl' : 'ltr'}>
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Logo */}
          <div className={`flex flex-col items-center ${isRTL ? 'md:items-end' : 'md:items-start'}`}>
            <img
              src="/logo2.jpeg"
              alt={t('ironcladLogo')}
              className="h-20 w-auto rounded mb-4"
            />
            <p className="text-gray-600 text-sm">{t('empoweringCyberSecurity')}</p>
          </div>

          {/* Contact Info */}
          <div className={`text-center ${isRTL ? 'md:text-right' : 'md:text-left'}`}>
            <h3 className="text-xl font-bold text-[#00ADEF] mb-4">{t('contact')}</h3>
            <address className="text-gray-600 not-italic">
              <p className="mt-2">
                <a href="tel:053-5274557" className="hover:text-[#00ADEF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#00ADEF] rounded">
                  {t('phone')}: 053-5274557
                </a>
              </p>
              <p className="mt-2">
                <a href="mailto:Info@Ironclad.co.il" className="hover:text-[#00ADEF] transition-colors focus:outline-none focus:ring-2 focus:ring-[#00ADEF] rounded">
                  {t('email')}: Info@Ironclad.co.il
                </a>
              </p>
            </address>
          </div>

          {/* Vision */}
          <div className={`text-center ${isRTL ? 'md:text-right' : 'md:text-left'}`}>
            <h3 className="text-xl font-bold text-[#00ADEF] mb-4">{t('vision')}</h3>
            <p className="text-gray-600 text-sm">
              {t('visionText')}
            </p>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="mt-12 pt-8 border-t border-gray-200">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-gray-500 text-sm">
              © {new Date().getFullYear()} Ironclad Systems. {t('allRights')}
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={onPrivacyClick}
                className="text-gray-500 hover:text-[#00ADEF] text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#00ADEF] rounded px-1"
              >
                {t('privacyPolicy')}
              </button>
              <span className="text-gray-300">|</span>
              <button
                onClick={onAccessibilityClick}
                className="text-gray-500 hover:text-[#00ADEF] text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-[#00ADEF] rounded px-1"
              >
                {t('accessibilityStatement')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Scroll to Top Button
const ScrollToTop = () => {
  const { t, language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  const isRTL = language === 'he';

  useEffect(() => {
    const toggleVisibility = () => {
      setIsVisible(window.scrollY > 300);
    };
    window.addEventListener('scroll', toggleVisibility);
    return () => window.removeEventListener('scroll', toggleVisibility);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  };

  return (
    isVisible && (
      <button
        onClick={scrollToTop}
        className={`fixed bottom-8 ${isRTL ? 'left-8' : 'right-8'} bg-[#00ADEF] hover:bg-blue-600 text-white p-3 rounded-full shadow-lg transition-all z-40 focus:outline-none focus:ring-2 focus:ring-blue-400`}
        aria-label={t('scrollToTop')}
      >
        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
        </svg>
      </button>
    )
  );
};

// Main App Component
function App() {
  const [language, setLanguage] = useState(() => {
    return localStorage.getItem('language') || 'he';
  });
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showAccessibility, setShowAccessibility] = useState(false);

  // Save language preference
  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'he' ? 'rtl' : 'ltr';
  }, [language]);

  // Translation function
  const t = (key) => translations[language][key] || key;

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      <div className="App">
        {/* Skip Link for Accessibility */}
        <a href="#main-content" className="skip-link">
          {t('skipToContent')}
        </a>

        <AccessibilityWidget />
        <Header />

        <main id="main-content">
          <HeroSection />
          <AboutSection />
          <StatisticsSection />
          <ServicesSection />
          <SolutionsSection />
          <ProductsSection />
          <CustomersSection />
        </main>

        <Footer
          onPrivacyClick={() => setShowPrivacy(true)}
          onAccessibilityClick={() => setShowAccessibility(true)}
        />

        <ScrollToTop />
        <CookieConsent />

        <PrivacyPolicyModal
          isOpen={showPrivacy}
          onClose={() => setShowPrivacy(false)}
        />
        <AccessibilityStatementModal
          isOpen={showAccessibility}
          onClose={() => setShowAccessibility(false)}
        />
      </div>
    </LanguageContext.Provider>
  );
}

export default App;
